import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ChartBar, Clock, CheckCircle, HourglassHigh, WarningCircle, Users, Lightning,
  ArrowRight, Trophy, Timer, Buildings, CaretDown, CaretUp, FilePdf, FileXls, CalendarBlank
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line
} from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

const COLORS = ["#6366F1", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#38BDF8", "#F97316", "#14B8A6"];
const STATUS_COLORS = {
  nuevo: "#3B82F6", en_proceso: "#F59E0B", resuelto: "#10B981", no_solucionado: "#EF4444"
};
const STATUS_LABELS = {
  nuevo: "Nuevo", en_proceso: "En Proceso", resuelto: "Resuelto", no_solucionado: "No Solucionado"
};
const BUCKET_COLORS = ["#10B981", "#22D3EE", "#6366F1", "#F59E0B", "#F97316", "#EF4444", "#991B1B"];

const DATE_PRESETS = [
  { key: "all", label: "Todos" },
  { key: "today", label: "Hoy" },
  { key: "7d", label: "7 dias" },
  { key: "month", label: "Este mes" },
  { key: "prev_month", label: "Mes anterior" },
  { key: "3m", label: "3 meses" },
  { key: "year", label: "Este ano" },
  { key: "custom", label: "Personalizado" },
];

function getPresetDates(preset) {
  const now = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  switch (preset) {
    case "today": return { from: fmt(now), to: fmt(now) };
    case "7d": { const d = new Date(now); d.setDate(d.getDate() - 7); return { from: fmt(d), to: fmt(now) }; }
    case "month": { const d = new Date(now.getFullYear(), now.getMonth(), 1); return { from: fmt(d), to: fmt(now) }; }
    case "prev_month": { const s = new Date(now.getFullYear(), now.getMonth() - 1, 1); const e = new Date(now.getFullYear(), now.getMonth(), 0); return { from: fmt(s), to: fmt(e) }; }
    case "3m": { const d = new Date(now); d.setMonth(d.getMonth() - 3); return { from: fmt(d), to: fmt(now) }; }
    case "year": { return { from: `${now.getFullYear()}-01-01`, to: fmt(now) }; }
    default: return { from: "", to: "" };
  }
}

function formatHours(h) {
  if (h === null || h === undefined) return "-";
  if (h < 1) return `${Math.round(h * 60)}min`;
  if (h < 24) return `${h.toFixed(1)}h`;
  const days = Math.floor(h / 24);
  const rem = h % 24;
  return `${days}d ${rem.toFixed(0)}h`;
}

export function TicketsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedEng, setExpandedEng] = useState(null);
  const [datePreset, setDatePreset] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusTypes, setStatusTypes] = useState([]);
  const { token } = useAuth();

  const fetchData = async (from, to) => {
    setLoading(true);
    try {
      const qs = (from && to) ? `?date_from=${from}&date_to=${to}` : "";
      const [res, stRes] = await Promise.all([
        axios.get(`${API}/tickets/stats/dashboard${qs}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/status-types`),
      ]);
      setData(res.data);
      setStatusTypes(stRes.data || []);
    } catch { toast.error("Error al cargar estadisticas"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData("", ""); }, [token]);

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset === "all") { setDateFrom(""); setDateTo(""); fetchData("", ""); }
    else if (preset === "custom") { /* wait */ }
    else { const { from, to } = getPresetDates(preset); setDateFrom(from); setDateTo(to); fetchData(from, to); }
  };

  const handleCustomApply = () => { if (dateFrom && dateTo) fetchData(dateFrom, dateTo); };

  const handleExport = async (type) => {
    try {
      // Use selected date range or default to current month
      let endpoint;
      if (type === "pdf") {
        let m, y;
        if (dateFrom) {
          const d = new Date(dateFrom);
          m = d.getMonth() + 1;
          y = d.getFullYear();
        } else {
          const now = new Date();
          m = now.getMonth() + 1;
          y = now.getFullYear();
        }
        endpoint = `/reports/monthly-pdf?month=${m}&year=${y}`;
      } else {
        endpoint = "/tickets/export/excel";
      }
      const res = await axios.get(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      const ext = type === "pdf" ? "pdf" : "xlsx";
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte_tickets_${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`${type.toUpperCase()} descargado`);
    } catch { toast.error(`Error al exportar ${type.toUpperCase()}`); }
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Cargando estadisticas...</div>;
  if (!data) return null;

  const resolvedPct = data.total > 0 ? ((data.resolved_tickets / data.total) * 100).toFixed(1) : 0;
  const assignedEngineers = data.engineer_stats.filter(e => e.id !== "unassigned");

  return (
    <div className="space-y-6" data-testid="tickets-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard de Tickets</h1>
          <p className="text-xs text-slate-400 mt-0.5">Analiticas de tiempos, ingenieros y rendimiento</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleExport("excel")} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="export-tickets-excel">
            <FileXls size={18} className="mr-1.5" /> Excel
          </Button>
          <Button variant="outline" onClick={() => handleExport("pdf")} className="text-red-700 border-red-300 hover:bg-red-50" data-testid="export-tickets-pdf">
            <FilePdf size={18} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {/* Date Filter Bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="tickets-date-filter-bar">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 mr-1">
            <CalendarBlank size={16} className="text-slate-400" />
            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Periodo:</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {DATE_PRESETS.map(p => (
              <button key={p.key} onClick={() => handlePresetChange(p.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${datePreset === p.key ? "bg-indigo-600 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                data-testid={`tickets-date-preset-${p.key}`}>
                {p.label}
              </button>
            ))}
          </div>
          {datePreset === "custom" && (
            <div className="flex items-center gap-2 ml-2">
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 text-xs w-36" />
              <span className="text-xs text-slate-400">a</span>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 text-xs w-36" />
              <Button size="sm" onClick={handleCustomApply} disabled={!dateFrom || !dateTo} className="h-8 px-3 text-xs">Aplicar</Button>
            </div>
          )}
          {datePreset !== "all" && dateFrom && dateTo && (
            <span className="text-[10px] text-indigo-600 font-medium ml-2 bg-indigo-50 px-2 py-1 rounded">
              {dateFrom} — {dateTo}
            </span>
          )}
        </div>
      </div>

      {loading && <div className="text-center py-4 text-slate-400 text-sm">Actualizando datos...</div>}

      {/* KPIs Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="tickets-kpis">
        <KpiCard icon={ChartBar} color="slate" label="Total Tickets" value={data.total} testId="kpi-total-tickets" />
        <KpiCard icon={HourglassHigh} color="amber" label="Abiertos" value={data.open_tickets} testId="kpi-open" />
        <KpiCard icon={CheckCircle} color="emerald" label="Resueltos" value={data.resolved_tickets} sub={`${resolvedPct}% tasa`} testId="kpi-resolved" />
        <KpiCard icon={Clock} color="indigo" label="Tiempo Prom." value={formatHours(data.avg_resolution_hours)} sub="resolucion" testId="kpi-avg-time" />
        <KpiCard icon={Lightning} color="cyan" label="Mas Rapido" value={formatHours(data.min_resolution_hours)} testId="kpi-fastest" />
        <KpiCard icon={WarningCircle} color="red" label="SLA > 24h" value={data.sla_breach_count} sub="sin resolver" testId="kpi-sla" />
      </div>

      {/* Status Pipeline - Dynamic from configured statuses */}
      <div className="bg-white border border-slate-200 p-5 rounded-xl" data-testid="status-pipeline">
        <h3 className="text-xs font-semibold text-slate-500 mb-4 uppercase tracking-wider">Pipeline por Estatus</h3>
        {(() => {
          // Build map from data
          const statusCountMap = {};
          (data.by_status || []).forEach(s => { statusCountMap[s.status] = s.count; });
          // Use statusTypes from config for order and labels; match by name
          const pipeline = statusTypes.length > 0
            ? statusTypes.map(st => ({
                key: st.name,
                label: st.name.charAt(0).toUpperCase() + st.name.slice(1).replace(/_/g, " "),
                color: st.color || STATUS_COLORS[st.name] || "#64748b",
                count: statusCountMap[st.name] || 0,
                pct: data.total > 0 ? ((statusCountMap[st.name] || 0) / data.total * 100).toFixed(1) : 0,
              }))
            : (data.by_status || []).map(s => ({
                key: s.status,
                label: STATUS_LABELS[s.status] || s.status,
                color: STATUS_COLORS[s.status] || "#64748b",
                count: s.count,
                pct: data.total > 0 ? (s.count / data.total * 100).toFixed(1) : 0,
              }));
          return (
            <div className="flex items-stretch gap-2">
              {pipeline.map((s, i) => (
                <div key={s.key} className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="flex-1 text-center py-4 px-2 rounded-xl border-2 transition-all hover:shadow-md"
                    style={{ borderColor: `${s.color}40`, backgroundColor: `${s.color}08` }}>
                    <p className="text-2xl font-bold leading-none" style={{ color: s.color }}>{s.count}</p>
                    <p className="text-[11px] font-semibold truncate mt-1" style={{ color: s.color }}>{s.label}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{s.pct}%</p>
                  </div>
                  {i < pipeline.length - 1 && <ArrowRight size={16} className="text-slate-300 flex-shrink-0" />}
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Priority + Support Type Breakdown */}
      {(data.by_priority?.length > 0 || data.by_support_type?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {data.by_priority?.length > 0 && (
            <div className="bg-white border border-slate-200 p-5 rounded-xl" data-testid="priority-breakdown">
              <h3 className="text-xs font-semibold text-slate-500 mb-4 uppercase tracking-wider">Por Prioridad</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={data.by_priority.map(p => ({ ...p, name: { critico: "Critico", alto: "Alto", normal: "Normal", bajo: "Bajo" }[p.priority] || p.priority }))}
                    cx="50%" cy="50%" labelLine={false} outerRadius={80} dataKey="count" nameKey="name"
                    label={({ name, count }) => `${name}: ${count}`}>
                    {data.by_priority.map((p, i) => (
                      <Cell key={`priority-${p.priority}`} fill={{ critico: "#EF4444", alto: "#F97316", normal: "#3B82F6", bajo: "#6B7280" }[p.priority] || COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          {data.by_support_type?.length > 0 && (
            <div className="bg-white border border-slate-200 p-5 rounded-xl" data-testid="support-type-breakdown">
              <h3 className="text-xs font-semibold text-slate-500 mb-4 uppercase tracking-wider">Por Tipo de Soporte</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.by_support_type} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                  <YAxis dataKey="type" type="category" tick={{ fontSize: 10 }} width={80} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="count" name="Tickets" radius={[0, 4, 4, 0]}>
                    {data.by_support_type.map((entry, i) => (
                      <Cell key={`support-${entry.type || i}`} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Charts Row 1: Time Buckets + Aging */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-slate-200 p-5 rounded-xl" data-testid="resolution-time-chart">
          <h3 className="text-xs font-semibold text-slate-500 mb-4 uppercase tracking-wider">Tiempos de Resolucion (distribucion)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.time_buckets}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Bar dataKey="count" name="Tickets Resueltos" radius={[4, 4, 0, 0]}>
                {data.time_buckets.map((b, i) => (
                  <Cell key={`bucket-${b.label || i}`} fill={BUCKET_COLORS[i % BUCKET_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 p-5 rounded-xl" data-testid="aging-chart">
          <h3 className="text-xs font-semibold text-slate-500 mb-4 uppercase tracking-wider">Envejecimiento de Tickets Abiertos</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.aging}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Bar dataKey="count" name="Tickets Abiertos" radius={[4, 4, 0, 0]}>
                {data.aging.map((a, i) => (
                  <Cell key={`aging-${a.label || i}`} fill={BUCKET_COLORS[i % BUCKET_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly Trend */}
      <div className="bg-white border border-slate-200 p-5 rounded-xl" data-testid="monthly-trend">
        <h3 className="text-xs font-semibold text-slate-500 mb-4 uppercase tracking-wider">Tendencia Mensual</h3>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={data.monthly_data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="month" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
            <Tooltip contentStyle={{ fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="creados" name="Creados" stroke="#6366F1" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="cerrados" name="Cerrados" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* === ENGINEERS PERFORMANCE === */}
      <div className="bg-white border border-indigo-100 rounded-xl p-5" data-testid="engineer-stats">
        <h3 className="text-xs font-semibold text-indigo-600 mb-4 uppercase tracking-wider flex items-center gap-2">
          <Users size={16} weight="bold" /> Rendimiento por Ingeniero
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3 text-[10px] text-slate-500 font-medium uppercase">Ingeniero</th>
                <th className="text-center py-2 px-3 text-[10px] text-slate-500 font-medium uppercase">Total</th>
                <th className="text-center py-2 px-3 text-[10px] text-slate-500 font-medium uppercase">Resueltos</th>
                <th className="text-center py-2 px-3 text-[10px] text-slate-500 font-medium uppercase">En Proceso</th>
                <th className="text-center py-2 px-3 text-[10px] text-slate-500 font-medium uppercase">Tasa Res.</th>
                <th className="text-center py-2 px-3 text-[10px] text-slate-500 font-medium uppercase">Tiempo Prom.</th>
                <th className="text-center py-2 px-3 text-[10px] text-slate-500 font-medium uppercase">Mas Rapido</th>
                <th className="text-center py-2 px-3 text-[10px] text-slate-500 font-medium uppercase">Mas Lento</th>
                <th className="text-center py-2 px-3 text-[10px] text-slate-500 font-medium uppercase">Prom. Abiertos</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {data.engineer_stats.map((eng) => (
                <EngRow key={eng.id} eng={eng} expanded={expandedEng === eng.id}
                  onToggle={() => setExpandedEng(expandedEng === eng.id ? null : eng.id)} />
              ))}
              {data.engineer_stats.length === 0 && (
                <tr><td colSpan={10} className="text-center py-6 text-slate-400">Sin datos</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Fastest & Slowest */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {data.fastest_resolutions.length > 0 && (
          <div className="bg-white border border-emerald-100 rounded-xl p-5" data-testid="fastest-resolutions">
            <h3 className="text-xs font-semibold text-emerald-600 mb-3 uppercase tracking-wider flex items-center gap-2">
              <Trophy size={16} weight="fill" /> Top 5 Resoluciones Mas Rapidas
            </h3>
            <div className="space-y-2">
              {data.fastest_resolutions.map((r, i) => (
                <div key={r.ticket_id} className="flex items-center gap-3 p-2.5 border border-emerald-100 bg-emerald-50/50 rounded-lg">
                  <span className="text-xs font-bold text-emerald-700 w-5">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-800 truncate">{r.ticket_number}</p>
                    <p className="text-[10px] text-slate-500">{r.company} - {r.engineer_name}</p>
                  </div>
                  <span className="text-sm font-bold text-emerald-700 whitespace-nowrap">{formatHours(r.hours)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {data.slowest_resolutions.length > 0 && (
          <div className="bg-white border border-red-100 rounded-xl p-5" data-testid="slowest-resolutions">
            <h3 className="text-xs font-semibold text-red-600 mb-3 uppercase tracking-wider flex items-center gap-2">
              <Timer size={16} weight="fill" /> Top 5 Resoluciones Mas Lentas
            </h3>
            <div className="space-y-2">
              {data.slowest_resolutions.map((r, i) => (
                <div key={r.ticket_id} className="flex items-center gap-3 p-2.5 border border-red-100 bg-red-50/50 rounded-lg">
                  <span className="text-xs font-bold text-red-700 w-5">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-800 truncate">{r.ticket_number}</p>
                    <p className="text-[10px] text-slate-500">{r.company} - {r.engineer_name}</p>
                  </div>
                  <span className="text-sm font-bold text-red-700 whitespace-nowrap">{formatHours(r.hours)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Company Stats */}
      <div className="bg-white border border-slate-200 p-5 rounded-xl" data-testid="company-stats">
        <h3 className="text-xs font-semibold text-slate-500 mb-4 uppercase tracking-wider flex items-center gap-2">
          <Buildings size={16} /> Tickets por Empresa / Cliente
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3 text-[10px] text-slate-500 font-medium uppercase">Empresa</th>
                <th className="text-center py-2 px-3 text-[10px] text-slate-500 font-medium uppercase">Total</th>
                <th className="text-center py-2 px-3 text-[10px] text-slate-500 font-medium uppercase">Resueltos</th>
                <th className="text-center py-2 px-3 text-[10px] text-slate-500 font-medium uppercase">Abiertos</th>
                <th className="text-center py-2 px-3 text-[10px] text-slate-500 font-medium uppercase">Tiempo Prom.</th>
              </tr>
            </thead>
            <tbody>
              {data.company_stats.map((c, i) => (
                <tr key={c.company || `company-${i}`} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="py-2 px-3 font-medium text-slate-800 text-xs">{c.company}</td>
                  <td className="py-2 px-3 text-center font-semibold text-slate-700">{c.total}</td>
                  <td className="py-2 px-3 text-center text-emerald-600 font-medium">{c.resueltos}</td>
                  <td className="py-2 px-3 text-center text-amber-600 font-medium">{c.abiertos}</td>
                  <td className="py-2 px-3 text-center">
                    {c.avg_resolution_hours != null ? (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.avg_resolution_hours <= 4 ? 'bg-emerald-100 text-emerald-700' : c.avg_resolution_hours <= 24 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                        {formatHours(c.avg_resolution_hours)}
                      </span>
                    ) : <span className="text-slate-400 text-xs">-</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function EngRow({ eng, expanded, onToggle }) {
  const getRateColor = (rate) => {
    if (rate >= 80) return "bg-emerald-100 text-emerald-700";
    if (rate >= 50) return "bg-amber-100 text-amber-700";
    return "bg-red-100 text-red-700";
  };
  const getTimeColor = (h) => {
    if (h == null) return "text-slate-400";
    if (h <= 4) return "text-emerald-600";
    if (h <= 24) return "text-amber-600";
    return "text-red-600";
  };

  const resolved = eng.resolved_tickets || [];

  return (
    <>
      <tr className={`border-b border-slate-50 hover:bg-slate-50 cursor-pointer ${eng.id === "unassigned" ? "opacity-60" : ""}`}
        onClick={onToggle} data-testid={`engineer-row-${eng.id}`}>
        <td className="py-2.5 px-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-[10px] font-bold">
              {eng.name.charAt(0).toUpperCase()}
            </div>
            <span className="font-medium text-slate-800 text-xs">{eng.name}</span>
          </div>
        </td>
        <td className="py-2.5 px-3 text-center font-semibold text-slate-700">{eng.total}</td>
        <td className="py-2.5 px-3 text-center text-emerald-600 font-medium">{eng.resueltos}</td>
        <td className="py-2.5 px-3 text-center text-amber-600 font-medium">{eng.en_proceso}</td>
        <td className="py-2.5 px-3 text-center">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getRateColor(eng.resolution_rate)}`}>
            {eng.resolution_rate}%
          </span>
        </td>
        <td className="py-2.5 px-3 text-center">
          <span className="text-xs font-semibold text-indigo-700">{formatHours(eng.avg_resolution_hours)}</span>
        </td>
        <td className="py-2.5 px-3 text-center text-xs text-emerald-600">{formatHours(eng.min_resolution_hours)}</td>
        <td className="py-2.5 px-3 text-center text-xs text-red-600">{formatHours(eng.max_resolution_hours)}</td>
        <td className="py-2.5 px-3 text-center text-xs text-slate-500">{formatHours(eng.avg_open_hours)}</td>
        <td className="py-2.5 px-3 text-center">
          {eng.id !== "unassigned" && (expanded ? <CaretUp size={14} /> : <CaretDown size={14} />)}
        </td>
      </tr>
      {expanded && eng.id !== "unassigned" && (
        <tr>
          <td colSpan={10} className="px-3 py-3 bg-indigo-50/40">
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <MiniStat label="Tickets Totales" value={eng.total} color="slate" />
              <MiniStat label="Resueltos" value={eng.resueltos} color="emerald" />
              <MiniStat label="Tasa de Resolución" value={`${eng.resolution_rate}%`} color="indigo" />
              <MiniStat label="Tiempo Prom. Resolución" value={formatHours(eng.avg_resolution_hours)} color="amber" />
            </div>
            {/* Individual ticket resolution times */}
            {resolved.length > 0 && (
              <div className="bg-white rounded-lg border border-indigo-100 overflow-hidden" data-testid={`eng-tickets-${eng.id}`}>
                <div className="px-3 py-2 bg-indigo-50 border-b border-indigo-100">
                  <p className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wider">Tiempo de Resolución por Incidente ({resolved.length})</p>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b">
                        <th className="text-left py-1.5 px-3 text-[9px] text-slate-400 font-medium">No. Ticket</th>
                        <th className="text-left py-1.5 px-3 text-[9px] text-slate-400 font-medium">Empresa / Cliente</th>
                        <th className="text-left py-1.5 px-3 text-[9px] text-slate-400 font-medium">Tipo</th>
                        <th className="text-right py-1.5 px-3 text-[9px] text-slate-400 font-medium">Tiempo Resolución</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {resolved.map(rt => (
                        <tr key={rt.ticket_id} className="hover:bg-slate-50" data-testid={`eng-ticket-${rt.ticket_id}`}>
                          <td className="py-1.5 px-3 font-mono font-medium text-slate-700">{rt.ticket_number}</td>
                          <td className="py-1.5 px-3 text-slate-600">{rt.company}</td>
                          <td className="py-1.5 px-3 text-slate-500">{rt.hardware_type || rt.software_type || "-"}</td>
                          <td className="py-1.5 px-3 text-right">
                            <span className={`font-semibold ${getTimeColor(rt.hours)}`}>{formatHours(rt.hours)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {resolved.length === 0 && (
              <p className="text-center text-xs text-slate-400 py-3">Sin tickets resueltos en este período</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function MiniStat({ label, value, color }) {
  const colorMap = {
    slate: "text-slate-700", emerald: "text-emerald-700",
    indigo: "text-indigo-700", amber: "text-amber-700"
  };
  return (
    <div className="bg-white rounded-lg p-2.5 border border-slate-100">
      <p className="text-[9px] text-slate-500 uppercase tracking-wider">{label}</p>
      <p className={`text-lg font-bold ${colorMap[color] || 'text-slate-700'}`}>{value}</p>
    </div>
  );
}

function KpiCard({ icon: Icon, color, label, value, sub, testId }) {
  const colors = {
    slate: { bg: "bg-slate-50", border: "border-slate-200", icon: "text-slate-400", value: "text-slate-900" },
    amber: { bg: "bg-amber-50", border: "border-amber-200", icon: "text-amber-400", value: "text-amber-700" },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", icon: "text-emerald-400", value: "text-emerald-700" },
    indigo: { bg: "bg-indigo-50", border: "border-indigo-200", icon: "text-indigo-400", value: "text-indigo-700" },
    cyan: { bg: "bg-cyan-50", border: "border-cyan-200", icon: "text-cyan-400", value: "text-cyan-700" },
    red: { bg: "bg-red-50", border: "border-red-200", icon: "text-red-400", value: "text-red-700" },
  };
  const c = colors[color] || colors.slate;
  return (
    <div className={`${c.bg} border ${c.border} p-4 rounded-xl`} data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon size={16} className={c.icon} weight="duotone" />
        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${c.value}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}
